import React, { useState, useEffect, useRef } from 'react';
import { TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import {
  Plus, Trash2, Calendar, Clock, Target,
  MessageCircle, FileText, RotateCcw, Search, Info,
  Receipt, AlertCircle, RefreshCw, Bot, Settings,
  Play, Pause, Zap, Bell, Ban, Gift, Banknote, Edit
} from 'lucide-react';
import { Switch } from '@/components/ui/switch';
import { toast } from '@/hooks/use-toast';
import { Client } from '@/types/client';
import { 
  BillingRule, 
  NewBillingRule, 
  BillingRuleFormData, 
  BILLING_EVENTS, 
  BILLING_MONTHS, 
  DAYS,
  DATE_MODES,
  PERIOD_DUE_MODES,
  EXECUTION_MODES,
  BillingEvent,
  BillingMonth,
  BillingCategory,
  BillingWindowStartConfig,
  BillingWindowStartResponse
} from '@/types/billing';
import { ReminderRule, NewReminderRule } from '@/types/reminder';
import { AtomicLoadingScreen } from '@/components/ui/atomic-loader';
import BillingCategoryToggle from './BillingCategoryToggle';
import WelcomeTab from './WelcomeTab';
import SouthWelcomeTab from './SouthWelcomeTab';
import RenovacaoTab from './RenovacaoTab';
import ReminderDueDatesManager from '@/components/ReminderDueDatesManager';
import BillingCalendar from './BillingCalendar';
import DayEventsList from './DayEventsList';
import BlockDayModal from './BlockDayModal';
import RescheduleEventModal from './RescheduleEventModal';
import WeekendSettingsCard from './WeekendSettingsCard';
import { useBillingCalendar } from '@/hooks/useBillingCalendar';
import { useWeekendSettings } from '@/hooks/useWeekendSettings';
import { DayEvent } from '@/types/billing-calendar';
import api from '@/lib/api';

interface BillingTabProps {
  client: Client | null;
}

const BillingTab: React.FC<BillingTabProps> = ({ client }) => {
  const [billingRules, setBillingRules] = useState<BillingRule[]>([]);
  const [reminderRules, setReminderRules] = useState<ReminderRule[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [automationStatus, setAutomationStatus] = useState<any>(null);
  const [automationLoading, setAutomationLoading] = useState(false);
  const [activeCategory, setActiveCategory] = useState<BillingCategory>('COBRANÇA');
  const [showCalendar, setShowCalendar] = useState(false);
  
  // Estados para window_start
  const [windowStartConfig, setWindowStartConfig] = useState<BillingWindowStartConfig>({});
  const [windowStartLoading, setWindowStartLoading] = useState(false);
  const [windowStartSaving, setWindowStartSaving] = useState(false);
  const [currentWindowStart, setCurrentWindowStart] = useState<string>('');

  // Estados para window_start de REVISTORIA (visível junto com COBRANÇA)
  const [currentWindowStartRevistoria, setCurrentWindowStartRevistoria] = useState<string>('');
  const [windowStartRevistoriaSaving, setWindowStartRevistoriaSaving] = useState(false);
  
  // Estados para modais
  const [blockDayModalOpen, setBlockDayModalOpen] = useState(false);
  const [rescheduleModalOpen, setRescheduleModalOpen] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<DayEvent | null>(null);
  
  // Hook do calendário
  const calendarHook = useBillingCalendar({
    clientId: client?.id || 0
  });
  const weekendSettingsHook = useWeekendSettings({ clientId: client?.id || 0 });
  
  // Estado do formulário
  const [formData, setFormData] = useState<BillingRuleFormData>({
    day: '1',
    event: 'COBRANÇA',
    due_day: '5',
    billing_month: 'atual',
    days_before_due: undefined,
    date_mode: 'fixed',
    execution_mode: 'fixed_day'
  });

  // Edit mode (reusar form inline para editar regras cadastradas)
  const [editingRuleId, setEditingRuleId] = useState<number | null>(null);
  const formSectionRef = useRef<HTMLDivElement | null>(null);

  // Defaults usados em reset (após salvar/cancelar). Depende da categoria ativa
  // para Cobrança usar 'COBRANÇA' como event default e para Lembrete não importa
  // pois o form de lembrete só usa days_before_due.
  const buildDefaultFormData = (): BillingRuleFormData => ({
    day: '1',
    event: activeCategory === 'LEMBRETE' ? 'LEMBRETE' : 'COBRANÇA',
    due_day: '5',
    billing_month: 'atual',
    days_before_due: undefined,
    date_mode: 'fixed',
    execution_mode: 'fixed_day',
  });

  const handleCancelEdit = () => {
    setEditingRuleId(null);
    setFormData(buildDefaultFormData());
  };

  const startEditReminder = (rule: ReminderRule) => {
    setEditingRuleId(rule.id);
    setFormData((prev) => ({
      ...prev,
      days_before_due: String(rule.days_before_due),
    }));
    setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  const startEditBilling = (rule: BillingRule) => {
    setEditingRuleId(rule.id);
    setFormData({
      day: rule.day != null ? String(rule.day) : '1',
      event: (rule.event as BillingEvent) ?? 'COBRANÇA',
      due_day: rule.due_day != null ? String(rule.due_day) : '5',
      billing_month: (rule.billing_month as BillingMonth) ?? 'atual',
      days_before_due: undefined,
      date_mode: rule.date_mode ?? 'fixed',
      due_day_inicio: rule.due_day_inicio != null ? String(rule.due_day_inicio) : undefined,
      due_day_fim: rule.due_day_fim != null ? String(rule.due_day_fim) : undefined,
      period_due_mode: rule.period_due_mode ?? undefined,
      dias_para_tras: rule.dias_para_tras != null ? String(rule.dias_para_tras) : undefined,
      incluir_dia_atual: rule.incluir_dia_atual ?? false,
      dias_vencidos_inicio: rule.dias_vencidos_inicio != null ? String(rule.dias_vencidos_inicio) : undefined,
      dias_vencidos_fim: rule.dias_vencidos_fim != null ? String(rule.dias_vencidos_fim) : undefined,
      execution_mode: rule.execution_mode ?? 'fixed_day',
      frequencia_intervalo: rule.frequencia_intervalo != null ? String(rule.frequencia_intervalo) : undefined,
      frequencia_dia_inicio: rule.frequencia_dia_inicio != null ? String(rule.frequencia_dia_inicio) : undefined,
    });
    setTimeout(() => {
      formSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 0);
  };

  // Filtrar regras por categoria
  // Para lembretes, usar reminderRules da nova API
  // Para cobranças, usar billingRules (já filtrado sem lembretes)
  const filteredRules = activeCategory === 'LEMBRETE' 
    ? reminderRules.map(reminder => ({
        id: reminder.id,
        client_id: reminder.client_id,
        day: 1, // Não usado para lembretes
        event: 'LEMBRETE' as BillingEvent,
        due_day: 1, // Não usado para lembretes
        billing_month: 'atual' as BillingMonth, // Não usado para lembretes
        window_start: reminder.window_start,
        days_before_due: reminder.days_before_due,
        is_active: reminder.is_active,
        created_at: reminder.created_at,
        updated_at: reminder.updated_at
      }))
    : billingRules;
  
  const filteredBillingRules = filteredRules as BillingRule[];

  // Ajustar formulário baseado na categoria
  const handleCategoryChange = (category: BillingCategory) => {
    setActiveCategory(category);
    setShowCalendar(false);
    if (category === 'LEMBRETE') {
      setFormData(prev => ({ ...prev, event: 'LEMBRETE', billing_month: 'atual' }));
    } else if (category === 'ANIVERSÁRIO') {
      setFormData(prev => ({ ...prev, event: 'COBRANÇA', billing_month: 'atual' }));
    } else {
      setFormData(prev => ({ ...prev, event: 'COBRANÇA', billing_month: 'atual' }));
    }
  };

  // Handlers do calendário
  const handleCalendarClick = () => {
    // Toggle do calendário: se estiver aberto, fecha; se estiver fechado, abre
    const newShowCalendar = !showCalendar;
    setShowCalendar(newShowCalendar);
    
    // Se estiver abrindo o calendário, carregar os dados automaticamente
    // O calendário mostra eventos de ambos os tipos (lembretes e cobranças)
    if (newShowCalendar && client) {
      calendarHook.refreshCalendar();
    }
  };

  const handleBlockDay = async (days: number[], reason?: string) => {
    if (!client?.id) return false;
    
    const year = calendarHook.currentDate.getFullYear();
    const month = calendarHook.currentDate.getMonth() + 1;
    
    return await calendarHook.blockDays({
      client_id: client.id,
      year,
      month,
      blocked_days: days,
      reason
    });
  };

  const handleRescheduleEvent = async (event: DayEvent) => {
    setSelectedEvent(event);
    setRescheduleModalOpen(true);
  };

  const handleConfirmReschedule = async (data: {
    client_id: number;
    billing_rule_id?: number;
    original_day: number;
    rescheduled_day: number;
    year: number;
    month: number;
    reason?: string;
  }) => {
    return await calendarHook.rescheduleEvent(data);
  };

  const handleCancelReschedule = async (rescheduleId: number) => {
    return await calendarHook.cancelReschedule(rescheduleId);
  };

  // Filtrar opções de eventos baseado na categoria
  const getEventOptions = () => {
    if (activeCategory === 'LEMBRETE') {
      return [{ value: 'LEMBRETE', label: 'LEMBRETE' }];
    }
    return BILLING_EVENTS.filter(event => event !== 'LEMBRETE').map(event => ({
      value: event,
      label: event
    }));
  };

  // Filtrar opções de meses baseado na categoria
  const getMonthOptions = () => {
    if (activeCategory === 'LEMBRETE') {
      // Para lembretes: apenas meses futuros (atual e próximo)
      return BILLING_MONTHS.filter(month => month.value === 'atual' || month.value === '+1');
    } else {
      // Para cobranças: apenas meses passados e atual (não futuro)
      return BILLING_MONTHS.filter(month => month.value !== '+1');
    }
  };

  // Carregar regras do cliente
  useEffect(() => {
    if (client?.id) {
      loadBillingRules();
      loadAutomationStatus();
      loadWindowStartConfig();
    }
  }, [client?.id]);

  // Atualizar horário atual quando a categoria mudar
  useEffect(() => {
    if (windowStartConfig) {
      const currentType = activeCategory === 'LEMBRETE' ? 'lembrete' : activeCategory === 'ANIVERSÁRIO' ? 'aniversario' : activeCategory === 'PAGAMENTO' ? 'pagamento' : 'cobranca';
      const currentValue = windowStartConfig[`window_start_${currentType}`] || '';
      const formattedValue = currentValue ? currentValue.substring(0, 5) : '';
      setCurrentWindowStart(formattedValue);
    }
  }, [activeCategory, windowStartConfig]);

  const loadBillingRules = async () => {
    if (!client?.id) return;

    setLoading(true);
    try {
      // Carregar cobranças da API antiga
      const billingResult = await api.get(`/billing-rules/client/${client.id}`) as any;
      const billingData = billingResult.data || [];
      // Filtrar apenas cobranças (remover lembretes da tabela antiga)
      setBillingRules(billingData.filter((rule: BillingRule) => rule.event !== 'LEMBRETE'));
      
      // Carregar lembretes da nova API
      try {
        const reminderResult = await api.get(`/reminder-rules/client/${client.id}`) as any;
        setReminderRules(reminderResult.data || []);
      } catch (reminderError) {
        console.error('Erro ao carregar lembretes (pode não existir ainda):', reminderError);
        // Se a API não existir ainda, deixar vazio
        setReminderRules([]);
      }
    } catch (error) {
      console.error('Erro ao carregar regras:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar regras de cobrança",
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };

  const loadAutomationStatus = async () => {
    if (!client?.id) return;

    setAutomationLoading(true);
    try {
      const result = await api.get(`/automation/status/${client.id}`) as any;
      setAutomationStatus(result.data);
    } catch (error) {
      console.error('Erro ao carregar status da automação:', error);
      toast({
        title: "Erro",
        description: "Erro ao carregar status da automação",
        variant: "destructive"
      });
    } finally {
      setAutomationLoading(false);
    }
  };

  const toggleAutomation = async () => {
    if (!client?.id || !automationStatus) return;

    setAutomationLoading(true);
    try {
      const newStatus = !automationStatus.client.is_automation_enabled;
      await api.put(`/automation/toggle/${client.id}`, {
        is_enabled: newStatus
      });
      
      setAutomationStatus(prev => ({
        ...prev,
        client: {
          ...prev.client,
          is_automation_enabled: newStatus
        }
      }));

      toast({
        title: "Sucesso",
        description: `Automação ${newStatus ? 'ativada' : 'desativada'} com sucesso`,
      });
    } catch (error) {
      console.error('Erro ao alterar automação:', error);
      toast({
        title: "Erro",
        description: "Erro ao alterar status da automação",
        variant: "destructive"
      });
    } finally {
      setAutomationLoading(false);
    }
  };

  const executeAutomation = async () => {
    if (!client?.id) return;

    setAutomationLoading(true);
    try {
      const result = await api.post(`/automation/execute/${client.id}`) as any;
      console.log('Resultado da automação:', result.data);
      
      toast({
        title: "Sucesso",
        description: "Automação executada com sucesso",
      });
    } catch (error) {
      console.error('Erro ao executar automação:', error);
      toast({
        title: "Erro",
        description: "Erro ao executar automação",
        variant: "destructive"
      });
    } finally {
      setAutomationLoading(false);
    }
  };

  const handleAddRule = async () => {
    if (!client?.id) return;

    setSaving(true);
    try {
      const isEditing = editingRuleId != null;
      if (activeCategory === 'LEMBRETE') {
        // Para lembretes, usar a nova API
        if (!formData.days_before_due) {
          toast({
            title: "Erro",
            description: "Por favor, informe quantos dias antes do vencimento",
            variant: "destructive"
          });
          setSaving(false);
          return;
        }

        const newReminder: NewReminderRule = {
          days_before_due: parseInt(formData.days_before_due)
        };

        if (isEditing && editingRuleId != null) {
          const result = await api.put(`/reminder-rules/${editingRuleId}`, newReminder) as any;
          setReminderRules(prev => prev.map(r => r.id === editingRuleId ? result.data : r));
          setEditingRuleId(null);
          setFormData(buildDefaultFormData());
          toast({
            title: "Sucesso",
            description: `Lembrete atualizado: ${newReminder.days_before_due} dias antes do vencimento`
          });
        } else {
          const result = await api.post('/reminder-rules', {
            client_id: client.id,
            ...newReminder
          }) as any;
          setReminderRules(prev => [...prev, result.data]);
          setFormData(buildDefaultFormData());
          toast({
            title: "Sucesso",
            description: `Lembrete adicionado: ${newReminder.days_before_due} dias antes do vencimento`
          });
        }
      } else {
        // Para cobranças
        const execMode = formData.execution_mode || 'fixed_day';
        const newRule: NewBillingRule = {
          day: execMode === 'frequency' ? (formData.frequencia_dia_inicio ? parseInt(formData.frequencia_dia_inicio) : 1) : parseInt(formData.day),
          event: formData.event,
          due_day: formData.date_mode === 'fixed' ? parseInt(formData.due_day) : parseInt(formData.day),
          billing_month: formData.billing_month
        };
        if (execMode === 'frequency') {
          newRule.execution_mode = 'frequency';
          newRule.frequencia_intervalo = parseInt(formData.frequencia_intervalo || '1');
          newRule.frequencia_dia_inicio = parseInt(formData.frequencia_dia_inicio || '1');
        }
        if (formData.date_mode) {
          newRule.date_mode = formData.date_mode;
          if (formData.date_mode === 'period_due') {
            newRule.period_due_mode = formData.period_due_mode || 'range';
            if (formData.period_due_mode === 'days_back') {
              newRule.dias_para_tras = parseInt(formData.dias_para_tras || '5');
              newRule.incluir_dia_atual = formData.incluir_dia_atual ?? false;
            } else {
              newRule.due_day_inicio = parseInt(formData.due_day_inicio || '1');
              newRule.due_day_fim = parseInt(formData.due_day_fim || '31');
            }
          } else if (formData.date_mode === 'period_overdue') {
            newRule.dias_vencidos_inicio = parseInt(formData.dias_vencidos_inicio || '6');
            newRule.dias_vencidos_fim = parseInt(formData.dias_vencidos_fim || '60');
          }
        }

        let resultData: BillingRule;
        if (isEditing && editingRuleId != null) {
          const result = await api.put(`/billing-rules/${editingRuleId}`, newRule) as any;
          resultData = result.data;
          setBillingRules(prev => prev.map(r => r.id === editingRuleId ? resultData : r));
          setEditingRuleId(null);
        } else {
          const result = await api.post('/billing-rules', {
            client_id: client.id,
            ...newRule
          }) as any;
          resultData = result.data;
          setBillingRules(prev => [...prev, resultData]);
        }

        // Reset form
        setFormData(buildDefaultFormData());

        const desc = formData.execution_mode === 'frequency'
          ? `Frequência: a cada ${newRule.frequencia_intervalo} dia(s) a partir do dia ${newRule.frequencia_dia_inicio}`
          : formData.date_mode === 'period_overdue'
            ? `Vencidos ${newRule.dias_vencidos_inicio}-${newRule.dias_vencidos_fim} dias`
            : formData.date_mode === 'period_due'
              ? (formData.period_due_mode === 'days_back'
                ? `Período ${newRule.dias_para_tras} dias atrás`
                : `Período venc. ${newRule.due_day_inicio}-${newRule.due_day_fim}`)
              : `DIA ${newRule.day}, ${newRule.event}, VENC ${newRule.due_day}, MÊS ${formData.billing_month?.toUpperCase()}`;
        toast({
          title: "Sucesso",
          description: `${isEditing ? 'Regra atualizada' : 'Regra adicionada'}: ${desc}`
        });
      }
    } catch (error: any) {
      console.error('Erro ao adicionar regra:', error);
      const errorMessage = error?.response?.data?.error || error?.response?.data?.details || 'Erro ao adicionar regra';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteRule = async (ruleId: number) => {
    setSaving(true);
    try {
      if (activeCategory === 'LEMBRETE') {
        await api.delete(`/reminder-rules/${ruleId}`);
        setReminderRules(prev => prev.filter(rule => rule.id !== ruleId));
        toast({
          title: "Sucesso",
          description: "Lembrete removido com sucesso"
        });
      } else {
        await api.delete(`/billing-rules/${ruleId}`);
        setBillingRules(prev => prev.filter(rule => rule.id !== ruleId));
        toast({
          title: "Sucesso",
          description: "Regra de cobrança removida com sucesso"
        });
      }
    } catch (error) {
      console.error('Erro ao deletar regra:', error);
      toast({
        title: "Erro",
        description: "Erro ao remover regra",
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  const handleToggleReminderActive = async (ruleId: number, currentStatus: boolean) => {
    try {
      setSaving(true);
      const result = await api.patch(`/reminder-rules/${ruleId}/toggle-active`) as any;
      
      // Atualizar com os dados retornados ou usar is_active diretamente
      const newStatus = result.data.is_active !== undefined ? result.data.is_active : (result.data.data?.is_active ?? !currentStatus);
      
      setReminderRules(prev => prev.map(rule => 
        rule.id === ruleId ? { ...rule, is_active: newStatus } : rule
      ));

      toast({
        title: "Sucesso",
        description: result.data.message || `Lembrete ${newStatus ? 'ativado' : 'desativado'} com sucesso`
      });
    } catch (error: any) {
      console.error('Erro ao alternar status do lembrete:', error);
      const errorMessage = error?.response?.data?.details || error?.response?.data?.error || 'Erro ao alterar status do lembrete';
      toast({
        title: "Erro",
        description: errorMessage,
        variant: "destructive"
      });
    } finally {
      setSaving(false);
    }
  };

  // Funções para window_start
  const loadWindowStartConfig = async () => {
    if (!client?.id) return;

    setWindowStartLoading(true);
    try {
      const [billingRes, birthdayRes, paymentRes] = await Promise.all([
        api.get(`/billing-rules/window-start/${client.id}`) as Promise<{ success: boolean; data?: BillingWindowStartConfig }>,
        api.get(`/birthday-rules/client/${client.id}`).catch(() => ({ success: false, data: null })) as Promise<{ success: boolean; data?: { window_start?: string; is_active?: boolean } | null }>,
        api.get(`/payment-rules/client/${client.id}`).catch(() => ({ success: false, data: null })) as Promise<{ success: boolean; data?: { window_start?: string; is_active?: boolean } | null }>
      ]);

      const config: BillingWindowStartConfig = billingRes.success && billingRes.data ? { ...billingRes.data } : {};

      if (birthdayRes.success && birthdayRes.data) {
        if (birthdayRes.data.window_start) config.window_start_aniversario = birthdayRes.data.window_start.substring(0, 5);
        config.is_active_aniversario = birthdayRes.data.is_active ?? true;
      }
      if (paymentRes.success && paymentRes.data) {
        if (paymentRes.data.window_start) config.window_start_pagamento = paymentRes.data.window_start.substring(0, 5);
        config.is_active_pagamento = paymentRes.data.is_active ?? true;
      }

      setWindowStartConfig(config);

      const currentType = activeCategory === 'LEMBRETE' ? 'lembrete' : activeCategory === 'ANIVERSÁRIO' ? 'aniversario' : activeCategory === 'PAGAMENTO' ? 'pagamento' : 'cobranca';
      const currentValue = config[`window_start_${currentType}`] || '';
      const formattedValue = currentValue ? String(currentValue).substring(0, 5) : '09:00';
      setCurrentWindowStart(formattedValue);

      // Revistoria: carregar valor separado (exibido junto da categoria COBRANÇA)
      const revistoriaValue = config.window_start_revistoria || '';
      const formattedRevistoria = revistoriaValue ? String(revistoriaValue).substring(0, 5) : '09:00';
      setCurrentWindowStartRevistoria(formattedRevistoria);
    } catch (error) {
      console.error('Erro ao carregar window_start:', error);
    } finally {
      setWindowStartLoading(false);
    }
  };

  const saveWindowStartRevistoria = async () => {
    if (!client?.id) return;

    setWindowStartRevistoriaSaving(true);
    try {
      const response = await api.put(`/billing-rules/window-start/${client.id}`, {
        window_start_revistoria: currentWindowStartRevistoria
      }) as any;

      if (response.success) {
        const savedValue = response.data?.data?.window_start_revistoria || currentWindowStartRevistoria;
        const formattedSavedValue = savedValue ? String(savedValue).substring(0, 5) : savedValue;

        setWindowStartConfig(prev => ({ ...prev, window_start_revistoria: savedValue }));
        setCurrentWindowStartRevistoria(formattedSavedValue);
        await loadWindowStartConfig();

        toast({
          title: "Sucesso",
          description: "Horário de execução para revistoria atualizado com sucesso"
        });
      }
    } catch (error: any) {
      console.error('Erro ao salvar window_start revistoria:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.error || "Erro ao atualizar horário de execução de revistoria",
        variant: "destructive"
      });
    } finally {
      setWindowStartRevistoriaSaving(false);
    }
  };

  const saveWindowStartConfig = async () => {
    if (!client?.id) return;

    setWindowStartSaving(true);
    try {
      const currentType = activeCategory === 'LEMBRETE' ? 'lembrete' : activeCategory === 'ANIVERSÁRIO' ? 'aniversario' : activeCategory === 'PAGAMENTO' ? 'pagamento' : 'cobranca';

      if (activeCategory === 'ANIVERSÁRIO') {
        const response = await api.put(`/birthday-rules/client/${client.id}`, {
          window_start: currentWindowStart,
          is_active: windowStartConfig.is_active_aniversario ?? true
        }) as any;

        if (response.success) {
          const savedValue = response.data?.data?.window_start || currentWindowStart;
          const formattedSavedValue = savedValue ? String(savedValue).substring(0, 5) : savedValue;

          setWindowStartConfig(prev => ({
            ...prev,
            window_start_aniversario: formattedSavedValue
          }));
          setCurrentWindowStart(formattedSavedValue);
          await loadWindowStartConfig();

          toast({
            title: "Sucesso",
            description: "Horário de execução para aniversário atualizado com sucesso"
          });
        }
      } else if (activeCategory === 'PAGAMENTO') {
        const response = await api.put(`/payment-rules/client/${client.id}`, {
          window_start: currentWindowStart,
          is_active: windowStartConfig.is_active_pagamento ?? true
        }) as any;

        if (response.success) {
          const savedValue = response.data?.data?.window_start || currentWindowStart;
          const formattedSavedValue = savedValue ? String(savedValue).substring(0, 5) : savedValue;

          setWindowStartConfig(prev => ({
            ...prev,
            window_start_pagamento: formattedSavedValue
          }));
          setCurrentWindowStart(formattedSavedValue);
          await loadWindowStartConfig();

          toast({
            title: "Sucesso",
            description: "Horário de execução para pagamento atualizado com sucesso"
          });
        }
      } else {
        const updateData = { [`window_start_${currentType}`]: currentWindowStart };
        const response = await api.put(`/billing-rules/window-start/${client.id}`, updateData) as any;

        if (response.success) {
          const savedValue = response.data?.data?.[`window_start_${currentType}`] || currentWindowStart;
          const formattedSavedValue = savedValue ? String(savedValue).substring(0, 5) : savedValue;

          setWindowStartConfig(prev => ({
            ...prev,
            [`window_start_${currentType}`]: savedValue
          }));
          setCurrentWindowStart(formattedSavedValue);
          await loadWindowStartConfig();

          toast({
            title: "Sucesso",
            description: `Horário de execução para ${activeCategory.toLowerCase()} atualizado com sucesso`
          });
        }
      }
    } catch (error: any) {
      console.error('Erro ao salvar window_start:', error);
      toast({
        title: "Erro",
        description: error.response?.data?.error || "Erro ao atualizar horário de execução",
        variant: "destructive"
      });
    } finally {
      setWindowStartSaving(false);
    }
  };

  const handleWindowStartChange = (value: string) => {
    setCurrentWindowStart(value);
  };

  const handleToggleAniversarioActive = async (checked: boolean) => {
    if (!client?.id) return;
    setWindowStartSaving(true);
    try {
      const windowStart = windowStartConfig.window_start_aniversario || currentWindowStart || '09:00';
      const response = await api.put(`/birthday-rules/client/${client.id}`, {
        window_start: windowStart.substring(0, 5),
        is_active: checked
      }) as any;
      if (response.success) {
        setWindowStartConfig(prev => ({ ...prev, is_active_aniversario: checked }));
        toast({
          title: 'Sucesso',
          description: checked ? 'Automação de aniversário ativada' : 'Automação de aniversário desativada'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao atualizar',
        variant: 'destructive'
      });
    } finally {
      setWindowStartSaving(false);
    }
  };

  const handleTogglePagamentoActive = async (checked: boolean) => {
    if (!client?.id) return;
    setWindowStartSaving(true);
    try {
      const windowStart = windowStartConfig.window_start_pagamento || currentWindowStart || '09:00';
      const response = await api.put(`/payment-rules/client/${client.id}`, {
        window_start: windowStart.substring(0, 5),
        is_active: checked
      }) as any;
      if (response.success) {
        setWindowStartConfig(prev => ({ ...prev, is_active_pagamento: checked }));
        toast({
          title: 'Sucesso',
          description: checked ? 'Automação de pagamento ativada' : 'Automação de pagamento desativada'
        });
      }
    } catch (error: any) {
      toast({
        title: 'Erro',
        description: error.response?.data?.error || 'Erro ao atualizar',
        variant: 'destructive'
      });
    } finally {
      setWindowStartSaving(false);
    }
  };

  const getBillingMonthLabel = (month: BillingMonth) => {
    return BILLING_MONTHS.find(m => m.value === month)?.label || month;
  };

  const getEventIcon = (event: BillingEvent) => {
    switch (event) {
      case 'COBRANÇA':
        return <Receipt className="w-5 h-5 text-white" />;
      case 'REVISTORIA':
        return <MessageCircle className="w-5 h-5 text-white" />;
      case 'REATIVAÇÃO':
        return <RotateCcw className="w-5 h-5 text-white" />;
      case 'RECUPERAÇÃO':
        return <Search className="w-5 h-5 text-white" />;
      default:
        return <FileText className="w-5 h-5 text-white" />;
    }
  };

  const getEventColor = (event: BillingEvent) => {
    switch (event) {
      case 'COBRANÇA':
        return 'bg-green-500';
      case 'REVISTORIA':
        return 'bg-blue-500';
      case 'REATIVAÇÃO':
        return 'bg-yellow-500';
      case 'RECUPERAÇÃO':
        return 'bg-red-500';
      default:
        return 'bg-gray-500';
    }
  };

  const getEventLightColor = (event: BillingEvent) => {
    switch (event) {
      case 'COBRANÇA':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'REVISTORIA':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'REATIVAÇÃO':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'RECUPERAÇÃO':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getEventDescription = (event: BillingEvent) => {
    switch (event) {
      case 'COBRANÇA':
        return 'Envio da mensagem mais boleto';
      case 'REVISTORIA':
        return 'Envio apenas da mensagem';
      case 'REATIVAÇÃO':
        return 'Envio do 2º template de mensagem';
      case 'RECUPERAÇÃO':
        return 'Envio do 3º template de mensagem';
      case 'LEMBRETE':
        return 'Envio de lembrete de vencimento';
      default:
        return 'Evento não definido';
    }
  };

  if (!client) {
    return (
      <TabsContent value="billing" className="space-y-4 mt-0">
        <div className="text-center text-gray-500 py-8">
          Selecione um cliente para configurar as regras de cobrança
        </div>
      </TabsContent>
    );
  }

  return (
    <TabsContent value="billing" className="space-y-6 mt-0">
      {/* Toggle de Categoria */}
      <BillingCategoryToggle 
        activeCategory={activeCategory}
        onCategoryChange={handleCategoryChange}
        onCalendarClick={handleCalendarClick}
        showCalendar={showCalendar}
      />

      {/* Calendário de Reagendamento - Layout completo similar ao Schedule */}
      {/* Mostra APENAS o calendário quando showCalendar é true, ocultando conteúdo de Lembrete/Cobrança */}
      {showCalendar && client ? (
        <div className="space-y-6">
          {/* Configurações de Dias Não Úteis */}
          <WeekendSettingsCard clientId={client.id} />
          
          {/* Grid com Calendário e Eventos */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Calendário à esquerda */}
            <div className="lg:col-span-1">
            <BillingCalendar
              currentDate={calendarHook.currentDate}
              calendarDays={calendarHook.calendarDays}
              calendarData={calendarHook.calendarData}
              onPreviousMonth={calendarHook.goToPreviousMonth}
              onNextMonth={calendarHook.goToNextMonth}
              onCurrentMonth={calendarHook.goToCurrentMonth}
              onDayClick={calendarHook.handleDayClick}
              selectedDay={calendarHook.selectedDay}
              weekendSettings={weekendSettingsHook.settings}
            />
            </div>

          {/* Lista de eventos à direita */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <CardTitle className="flex items-center gap-2">
                    <Calendar className="h-5 w-5" />
                    {client.trade_name || client.legal_name} - Dia {calendarHook.selectedDay || 'Selecione um dia'}
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    {calendarHook.dayEvents && (
                      <Badge variant="outline" className="bg-green-100 text-green-600">
                        {calendarHook.dayEvents.events.length} evento{calendarHook.dayEvents.events.length !== 1 ? 's' : ''}
                      </Badge>
                    )}
                    {calendarHook.dayEvents && !calendarHook.dayEvents.isBlocked && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => setBlockDayModalOpen(true)}
                        className="text-red-600 hover:text-red-700 hover:bg-red-50"
                      >
                        <Ban className="h-4 w-4 mr-2" />
                        Bloquear Dia
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => calendarHook.refreshCalendar()}
                      disabled={calendarHook.loading}
                    >
                      <RefreshCw className={`h-4 w-4 mr-2 ${calendarHook.loading ? 'animate-spin' : ''}`} />
                      Atualizar
                    </Button>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <DayEventsList
                  dayEvents={calendarHook.dayEvents}
                  onBlockDay={() => setBlockDayModalOpen(true)}
                  onRescheduleEvent={handleRescheduleEvent}
                  onCancelReschedule={handleCancelReschedule}
                  loading={calendarHook.loading}
                />
              </CardContent>
            </Card>
          </div>
        </div>
        </div>
      ) : (
        <>

      {/* Para ANIVERSÁRIO: mostrar apenas Horário de Execução - card mais abaixo */}
      {/* Legenda dos Eventos - apenas para Cobrança (não para Aniversário) */}
      {activeCategory === 'COBRANÇA' && (
        <Card className="border-l-4 border-l-indigo-500">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Info className="h-5 w-5 text-indigo-600" />
            Legenda dos Eventos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="flex items-start gap-3 p-3 rounded-lg border-2 bg-green-100 text-green-800 border-green-200">
              <div className="w-8 h-8 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Receipt className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm">COBRANÇA</h4>
                <p className="text-xs opacity-90 mt-1">Envio da mensagem mais boleto</p>
                <p className="text-xs font-medium mt-1 opacity-75">Para vencimentos dentro da carência</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border-2 bg-blue-100 text-blue-800 border-blue-200">
              <div className="w-8 h-8 rounded-full bg-blue-500 flex items-center justify-center flex-shrink-0">
                <MessageCircle className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm">REVISTORIA</h4>
                <p className="text-xs opacity-90 mt-1">Envio apenas da mensagem</p>
                <p className="text-xs font-medium mt-1 opacity-75">Para boletos vencidos até 30 dias</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border-2 bg-yellow-100 text-yellow-800 border-yellow-200">
              <div className="w-8 h-8 rounded-full bg-yellow-500 flex items-center justify-center flex-shrink-0">
                <RotateCcw className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm">REATIVAÇÃO</h4>
                <p className="text-xs opacity-90 mt-1">Envio do 2º template de mensagem</p>
                <p className="text-xs font-medium mt-1 opacity-75">Para boletos vencidos de 31 até 60 dias</p>
              </div>
            </div>

            <div className="flex items-start gap-3 p-3 rounded-lg border-2 bg-red-100 text-red-800 border-red-200">
              <div className="w-8 h-8 rounded-full bg-red-500 flex items-center justify-center flex-shrink-0">
                <Search className="w-4 h-4 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="font-semibold text-sm">RECUPERAÇÃO</h4>
                <p className="text-xs opacity-90 mt-1">Envio do 3º template de mensagem</p>
                <p className="text-xs font-medium mt-1 opacity-75">Para boletos acima de 61 dias vencidos</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Card Explicativo para Lembretes */}
      {activeCategory === 'LEMBRETE' && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bell className="h-5 w-5 text-blue-600" />
              Configuração de Lembretes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                <strong>Lembretes</strong> são enviados <strong>ANTES</strong> do vencimento para avisar o cliente sobre pagamentos futuros.
              </p>
                <p>
                Configure <strong>quantos dias antes do vencimento</strong> o lembrete deve ser enviado. O sistema executará automaticamente todos os dias no horário configurado.
              </p>
              <p className="text-blue-600 font-medium">
                Exemplo: Se configurar 5 dias, o lembrete será enviado automaticamente 5 dias antes de cada vencimento
              </p>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Régua de Boas-Vindas — variante South (webhook) ou Hinova (cron) */}
      {activeCategory === 'BOAS-VINDAS' && (
        (client?.system_management === 'south' || client?.system_management === 'south-v0')
          ? <SouthWelcomeTab clientId={client?.id || 0} />
          : <WelcomeTab clientId={client?.id || 0} />
      )}

      {/* Régua de Renovação (cron + Hinova + Meta) — stack independente da de Boas-Vindas */}
      {activeCategory === 'RENOVAÇÃO' && (
        <RenovacaoTab clientId={client?.id || 0} />
      )}

      {/* Card Explicativo para Aniversário */}
      {activeCategory === 'ANIVERSÁRIO' && (
        <Card className="border-l-4 border-l-rose-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Gift className="h-5 w-5 text-rose-600" />
              Automação de Aniversários (Hinova SGA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                Configure o <strong>horário de início</strong> para envio de mensagens de parabéns aos aniversariantes do dia.
              </p>
              <p>
                O sistema busca diariamente os aniversariantes no Hinova SGA (endpoint associado/aniversariante) e envia as mensagens no horário configurado. Configure a estratégia em <strong>Estratégias</strong> (template ou fluxo).
              </p>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <Label htmlFor="aniversario-ativo" className="text-sm font-medium text-gray-700">Ativar envio de aniversário</Label>
                <Switch
                  id="aniversario-ativo"
                  checked={windowStartConfig.is_active_aniversario ?? true}
                  onCheckedChange={handleToggleAniversarioActive}
                  disabled={windowStartSaving}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Card Explicativo para Pagamento */}
      {activeCategory === 'PAGAMENTO' && (
        <Card className="border-l-4 border-l-emerald-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Banknote className="h-5 w-5 text-emerald-600" />
              Automação de Pagamentos (Hinova SGA)
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3 text-sm text-gray-600">
              <p>
                Configure o <strong>horário de início</strong> para envio de mensagens de confirmação de pagamento.
              </p>
              <p>
                O sistema busca diariamente os <strong>pagamentos do dia anterior</strong> no Hinova SGA (listar por período de pagamento) e envia as mensagens no horário configurado. Configure a estratégia em <strong>Estratégias</strong> (template ou fluxo) para o tipo Pagamento.
              </p>
              <div className="flex items-center justify-between pt-2 border-t border-gray-200">
                <Label htmlFor="pagamento-ativo" className="text-sm font-medium text-gray-700">Ativar envio de pagamento</Label>
                <Switch
                  id="pagamento-ativo"
                  checked={windowStartConfig.is_active_pagamento ?? true}
                  onCheckedChange={handleTogglePagamentoActive}
                  disabled={windowStartSaving}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Controle de Automação - Apenas para cobranças (não para lembrete ou aniversário) */}
      {automationStatus && activeCategory === 'COBRANÇA' && (
        <Card className="border-l-4 border-l-purple-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Bot className="h-5 w-5 text-purple-600" />
              Automação Inteligente - Cobranças
              <Badge 
                variant={automationStatus.client.is_automation_enabled ? "default" : "secondary"}
                className={automationStatus.client.is_automation_enabled ? "bg-green-100 text-green-800" : ""}
              >
                {automationStatus.client.is_automation_enabled ? "ATIVA" : "INATIVA"}
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Informações da Automação */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Configuração Atual</h4>
                  <div className="space-y-1 text-xs">
                    <div className="flex justify-between">
                      <span>Tipo:</span>
                      <Badge variant="outline" className="text-xs">
                        {automationStatus.client.automation_type?.toUpperCase() || 'HINOVA'}
                      </Badge>
                    </div>
                    <div className="flex justify-between">
                      <span>Sistema:</span>
                      <Badge variant="outline" className="text-xs">
                        {automationStatus.is_hinova_client ? 'HINOVA' : 'SOUTH'}
                      </Badge>
                    </div>
                    {automationStatus.whatsapp_connection && (
                      <div className="flex justify-between">
                        <span>Conexão:</span>
                        <span className="font-mono text-xs">{automationStatus.whatsapp_connection.name}</span>
                      </div>
                    )}
                  </div>
                </div>

                <div className="bg-gray-50 p-4 rounded-lg">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">Status da Automação</h4>
                  <div className="space-y-1 text-xs">
                    {automationStatus.whatsapp_connection ? (
                      <>
                        <div className="flex justify-between">
                          <span>Regras:</span>
                          <Badge variant="outline" className="text-xs">
                            {automationStatus.billing_rules?.length || 0} configuradas
                          </Badge>
                        </div>
                        {automationStatus.client.last_automation_execution && (
                          <>
                            <div className="flex justify-between">
                              <span>Última execução:</span>
                              <span className="font-mono text-xs">
                                {new Date(automationStatus.client.last_automation_execution).toLocaleDateString('pt-BR')}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span>Execuções:</span>
                              <Badge variant="outline" className="text-xs">
                                #{automationStatus.client.automation_execution_count || 0}
                              </Badge>
                            </div>
                          </>
                        )}
                      </>
                    ) : (
                      <div className="text-red-600 text-xs">Nenhuma conexão WhatsApp oficial ativa</div>
                    )}
                  </div>
                </div>
              </div>

              {/* Como Funciona - Dinâmico por categoria */}
              <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 rounded-lg border border-purple-200">
                <h4 className="font-semibold text-sm text-purple-800 mb-2 flex items-center gap-2">
                  <Zap className="h-4 w-4" />
                  Como Funciona a Automação - Cobranças
                </h4>
                <div className="text-xs text-purple-700 space-y-1">
                  {false ? (
                    <>
                      <p><strong>Para Lembretes:</strong></p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li><strong>Execução única por dia:</strong> Executa apenas uma vez no horário de início da conexão oficial</li>
                        <li><strong>Verificação por minuto:</strong> A cada minuto, verifica se chegou o horário exato de execução</li>
                        <li><strong>Conexão oficial:</strong> Usa apenas a conexão WhatsApp do tipo "oficial" (não extra_oficial)</li>
                        <li><strong>Regras de lembrete:</strong> Verifica as regras de lembrete configuradas para o dia atual</li>
                        <li><strong>Filtro automático:</strong> Quando encontra evento "LEMBRETE", aplica filtro para vencimentos futuros</li>
                        <li><strong>Integração Hinova:</strong> Envia dados estruturados com formato: datainicio/datafim</li>
                        <li><strong>Template especial:</strong> Usa template especial para eventos de LEMBRETE</li>
                        <li><strong>Antecedência:</strong> Calcula automaticamente os dias de antecedência baseado na configuração</li>
                      </ul>
                    </>
                  ) : (
                    <>
                      <p><strong>Para Cobranças:</strong></p>
                      <ul className="list-disc list-inside ml-2 space-y-1">
                        <li><strong>Execução única por dia:</strong> Executa apenas uma vez no horário de início da conexão oficial</li>
                        <li><strong>Verificação por minuto:</strong> A cada minuto, verifica se chegou o horário exato de execução</li>
                        <li><strong>Conexão oficial:</strong> Usa apenas a conexão WhatsApp do tipo "oficial" (não extra_oficial)</li>
                        <li><strong>Regras de cobrança:</strong> Verifica as regras de cobrança configuradas para o dia atual</li>
                        <li><strong>Filtro automático:</strong> Quando encontra evento "COBRANÇA", aplica filtro "cobrança na carência"</li>
                        <li><strong>Integração Hinova:</strong> Envia dados estruturados com formato: datainicio/datafim</li>
                        <li><strong>Template especial:</strong> Usa template especial para eventos de COBRANÇA</li>
                        <li><strong>Subtipos:</strong> Processa REVISTORIA, REATIVAÇÃO e RECUPERAÇÃO conforme configurado</li>
                      </ul>
                    </>
                  )}
                </div>
              </div>

              {/* Controles */}
              <div className="flex gap-3 pt-2">
                <Button
                  onClick={toggleAutomation}
                  disabled={automationLoading}
                  variant={automationStatus.client.is_automation_enabled ? "destructive" : "default"}
                  className="flex-1"
                >
                  {automationLoading ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Processando...
                    </>
                  ) : (
                    <>
                      {automationStatus.client.is_automation_enabled ? (
                        <>
                          <Pause className="w-4 h-4 mr-2" />
                          Desativar Automação
                        </>
                      ) : (
                        <>
                          <Play className="w-4 h-4 mr-2" />
                          Ativar Automação de Cobranças
                        </>
                      )}
                    </>
                  )}
                </Button>

                {automationStatus.client.is_automation_enabled && (
                  <Button
                    onClick={executeAutomation}
                    disabled={automationLoading}
                    variant="outline"
                    className="flex-1"
                  >
                    <Settings className="w-4 h-4 mr-2" />
                    Executar Agora
                  </Button>
                )}
              </div>

              {/* Status das Regras - Filtradas por categoria */}
              {automationStatus.billing_rules && automationStatus.billing_rules.length > 0 && (
                <div className="mt-4">
                  <h4 className="font-semibold text-sm text-gray-700 mb-2">
                    Regras Configuradas - Cobranças
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-2">
                    {automationStatus.billing_rules
                      .filter((rule: any) => {
                        return rule.event !== 'LEMBRETE';
                      })
                      .map((rule: any) => (
                      <div key={rule.id} className="bg-white p-2 rounded border text-xs">
                        <div className="flex items-center justify-between">
                          <span className="font-mono">DIA {rule.day}</span>
                          <Badge 
                            variant="outline" 
                            className={`text-xs ${
                              rule.event === 'COBRANÇA' ? 'bg-green-100 text-green-800' : 
                              rule.event === 'REVISTORIA' ? 'bg-blue-100 text-blue-800' :
                              'bg-gray-100 text-gray-800'
                            }`}
                          >
                            {rule.event}
                          </Badge>
                        </div>
                        <div className="text-gray-600 mt-1">
                          {(rule.execution_mode || 'fixed_day') === 'frequency'
                            ? `A cada ${rule.frequencia_intervalo ?? '?'} dia(s) a partir do ${rule.frequencia_dia_inicio ?? '1'}`
                            : (rule.date_mode || 'fixed') === 'period_overdue'
                              ? `Vencidos ${rule.dias_vencidos_inicio ?? '?'}-${rule.dias_vencidos_fim ?? '?'} dias`
                              : (rule.date_mode || 'fixed') === 'period_due'
                                ? rule.period_due_mode === 'days_back'
                                  ? `Período ${rule.dias_para_tras ?? '?'} dias atrás`
                                  : `Período venc. ${rule.due_day_inicio ?? '?'}-${rule.due_day_fim ?? '?'}`
                                : `Venc: ${rule.due_day} • Mês: ${rule.billing_month}`}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Seção Horário de Execução - Dinâmica por categoria (não mostrar para Boas-Vindas: WelcomeTab tem o próprio horário) */}
      {activeCategory !== 'BOAS-VINDAS' && (
      <Card className="border-l-4 border-l-orange-500">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <Clock className="h-5 w-5 text-orange-600" />
            Horário de Execução - {activeCategory}
            <span className="text-xs bg-orange-100 text-orange-800 px-2 py-1 rounded-full ml-2">
              Configurável
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-sm text-gray-600 mb-4">
              Configure o horário de início para {activeCategory.toLowerCase()}. 
              O horário deve estar dentro da janela configurada em Rate Limits.
            </div>

            {/* Campo de horário com botão ao lado */}
            <div className={`p-4 rounded-lg border-2 ${
              activeCategory === 'LEMBRETE' 
                ? 'border-purple-200 bg-purple-50' 
                : activeCategory === 'ANIVERSÁRIO'
                ? 'border-rose-200 bg-rose-50'
                : activeCategory === 'PAGAMENTO'
                ? 'border-emerald-200 bg-emerald-50'
                : 'border-green-200 bg-green-50'
            }`}>
              <div className="flex items-center gap-2 mb-3">
                {activeCategory === 'LEMBRETE' ? (
                  <Bell className="h-5 w-5 text-purple-600" />
                ) : activeCategory === 'ANIVERSÁRIO' ? (
                  <Gift className="h-5 w-5 text-rose-600" />
                ) : activeCategory === 'PAGAMENTO' ? (
                  <Banknote className="h-5 w-5 text-emerald-600" />
                ) : (
                  <Receipt className="h-5 w-5 text-green-600" />
                )}
                <Label className={`text-sm font-medium ${
                  activeCategory === 'LEMBRETE' ? 'text-purple-800' : activeCategory === 'ANIVERSÁRIO' ? 'text-rose-800' : activeCategory === 'PAGAMENTO' ? 'text-emerald-800' : 'text-green-800'
                }`}>
                  Horário de Início - {activeCategory}
                </Label>
              </div>
              
              <div className="flex items-center gap-2">
                <Input
                  type="time"
                  value={currentWindowStart}
                  onChange={(e) => handleWindowStartChange(e.target.value)}
                  className="flex-1"
                  placeholder="09:00"
                />
                <Button
                  onClick={saveWindowStartConfig}
                  disabled={windowStartSaving || windowStartLoading}
                  className={`${
                    activeCategory === 'LEMBRETE' 
                      ? 'bg-purple-600 hover:bg-purple-700' 
                      : activeCategory === 'ANIVERSÁRIO'
                      ? 'bg-rose-600 hover:bg-rose-700'
                      : activeCategory === 'PAGAMENTO'
                      ? 'bg-emerald-600 hover:bg-emerald-700'
                      : 'bg-green-600 hover:bg-green-700'
                  } whitespace-nowrap`}
                >
                  {windowStartSaving ? (
                    <>
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    <>
                      <Clock className="w-4 h-4 mr-2" />
                      Salvar
                    </>
                  )}
                </Button>
              </div>
              
                <div className={`text-xs mt-2 ${
                  activeCategory === 'LEMBRETE' ? 'text-purple-600' : activeCategory === 'ANIVERSÁRIO' ? 'text-rose-600' : activeCategory === 'PAGAMENTO' ? 'text-emerald-600' : 'text-green-600'
                }`}>
                  Deve estar dentro da janela configurada em Rate Limits
                </div>
            </div>

          </div>
        </CardContent>
      </Card>
      )}

      {/* Seção Horário de Execução - REVISTORIA (visível apenas na categoria COBRANÇA) */}
      {activeCategory === 'COBRANÇA' && (
        <Card className="border-l-4 border-l-blue-500">
          <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-lg">
              <Clock className="h-5 w-5 text-blue-600" />
              Horário de Execução - REVISTORIA
              <span className="text-xs bg-blue-100 text-blue-800 px-2 py-1 rounded-full ml-2">
                Configurável
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              <div className="text-sm text-gray-600 mb-4">
                Configure o horário de início para revistoria. Aplica-se a todas as regras com evento REVISTORIA.
                O horário deve estar dentro da janela configurada em Rate Limits.
              </div>

              <div className="p-4 rounded-lg border-2 border-blue-200 bg-blue-50">
                <div className="flex items-center gap-2 mb-3">
                  <Receipt className="h-5 w-5 text-blue-600" />
                  <Label className="text-sm font-medium text-blue-800">
                    Horário de Início - REVISTORIA
                  </Label>
                </div>

                <div className="flex items-center gap-2">
                  <Input
                    type="time"
                    value={currentWindowStartRevistoria}
                    onChange={(e) => setCurrentWindowStartRevistoria(e.target.value)}
                    className="flex-1"
                    placeholder="09:00"
                  />
                  <Button
                    onClick={saveWindowStartRevistoria}
                    disabled={windowStartRevistoriaSaving || windowStartLoading}
                    className="bg-blue-600 hover:bg-blue-700 whitespace-nowrap"
                  >
                    {windowStartRevistoriaSaving ? (
                      <>
                        <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                        Salvando...
                      </>
                    ) : (
                      <>
                        <Clock className="w-4 h-4 mr-2" />
                        Salvar
                      </>
                    )}
                  </Button>
                </div>

                <div className="text-xs mt-2 text-blue-600">
                  Deve estar dentro da janela configurada em Rate Limits
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Dicas de Uso - não mostrar para Aniversário ou Pagamento */}
      {activeCategory !== 'ANIVERSÁRIO' && activeCategory !== 'PAGAMENTO' && (
      <Card className="border-l-4 border-l-amber-500">
        <CardHeader className="pb-4">
          <CardTitle className="flex items-center gap-2 text-lg">
            <AlertCircle className="h-5 w-5 text-amber-600" />
            Dicas de Uso
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 p-4 rounded-lg border border-amber-200">
            <p className="text-sm text-amber-800 leading-relaxed">
              <strong>Cobrança</strong> para vencimentos dentro da carência • 
              <strong> Revistoria</strong> para boletos vencidos até 30 dias • 
              <strong> Reativação</strong> de 31 até 60 dias • 
              <strong> Recuperação</strong> para boletos acima de 61 dias vencidos
            </p>
          </div>
        </CardContent>
      </Card>
      )}

      {/* Gerenciador de Datas de Vencimento (apenas para lembretes) */}
      {activeCategory === 'LEMBRETE' && client && (
        <div className="mt-6">
          <ReminderDueDatesManager 
            clientId={client.id} 
            clientName={client.trade_name || client.legal_name}
          />
        </div>
      )}

      {/* Formulário para adicionar nova regra - não mostrar para Aniversário, Pagamento ou Boas-Vindas */}
      {activeCategory !== 'ANIVERSÁRIO' && activeCategory !== 'PAGAMENTO' && activeCategory !== 'BOAS-VINDAS' && (
      <div ref={formSectionRef}><Card className="border-l-4 border-l-green-500">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Plus className="h-5 w-5 text-green-600" />
            {activeCategory === 'LEMBRETE' ? 'Adicionar Novo Lembrete' : 'Adicionar Nova Regra de Cobrança'}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {activeCategory === 'LEMBRETE' ? (
            // Formulário simplificado para lembretes
            <div className="space-y-4">
              <div>
                <Label htmlFor="days_before_due" className="flex items-center gap-2">
                  <Calendar className="h-4 w-4" />
                  Dias antes do vencimento
                </Label>
                <Input
                  id="days_before_due"
                  type="number"
                  min="0"
                  value={formData.days_before_due || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, days_before_due: e.target.value }))}
                  placeholder="Ex: 5 (para enviar 5 dias antes do vencimento)"
                  className="mt-1"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Informe quantos dias antes do vencimento o lembrete deve ser enviado
                </p>
              </div>

              <div className="mt-6 flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Preview:</span> 
                  <Badge variant="outline" className="ml-2 font-mono">
                    {formData.days_before_due 
                      ? `${formData.days_before_due} dias antes do vencimento`
                      : 'Informe os dias antes do vencimento'}
                  </Badge>
                </div>
              </div>
            </div>
          ) : (
            // Formulário completo para cobranças
            <div className="space-y-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <Label className="flex items-center gap-2">
                    <Calendar className="h-4 w-4" />
                    Modo de execução
                  </Label>
                  <Select value={formData.execution_mode || 'fixed_day'} onValueChange={(value: 'fixed_day' | 'frequency') => setFormData(prev => ({ ...prev, execution_mode: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Modo" />
                    </SelectTrigger>
                    <SelectContent>
                      {EXECUTION_MODES.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {(formData.execution_mode || 'fixed_day') === 'fixed_day' && (
                  <div>
                    <Label htmlFor="day">Dia (execução)</Label>
                    <Select value={formData.day} onValueChange={(value) => setFormData(prev => ({ ...prev, day: value }))}>
                      <SelectTrigger>
                        <SelectValue placeholder="Dia" />
                      </SelectTrigger>
                      <SelectContent>
                        {DAYS.map(day => (
                          <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}
                {(formData.execution_mode || 'fixed_day') === 'frequency' && (
                  <>
                    <div>
                      <Label>Intervalo (X em X dias)</Label>
                      <Input type="number" min="1" value={formData.frequencia_intervalo ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, frequencia_intervalo: e.target.value }))} placeholder="Ex: 1 = diário, 2 = a cada 2 dias" />
                    </div>
                    <div>
                      <Label>Dia inicial</Label>
                      <Select value={formData.frequencia_dia_inicio ?? '1'} onValueChange={(v) => setFormData(prev => ({ ...prev, frequencia_dia_inicio: v }))}>
                        <SelectTrigger><SelectValue /></SelectTrigger>
                        <SelectContent>
                          {DAYS.map(day => <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                  </>
                )}
                <div>
                  <Label htmlFor="event" className="flex items-center gap-2">
                    <Target className="h-4 w-4" />
                    Evento
                  </Label>
                  <Select value={formData.event} onValueChange={(value: BillingEvent) => setFormData(prev => ({ ...prev, event: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Evento" />
                    </SelectTrigger>
                    <SelectContent>
                      {getEventOptions().map(event => (
                        <SelectItem key={event.value} value={event.value}>{event.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="col-span-2">
                  <Label className="flex items-center gap-2">
                    <Clock className="h-4 w-4" />
                    Modalidade de data
                  </Label>
                  <Select value={formData.date_mode || 'fixed'} onValueChange={(value: 'fixed' | 'period_due' | 'period_overdue') => setFormData(prev => ({ ...prev, date_mode: value }))}>
                    <SelectTrigger>
                      <SelectValue placeholder="Modalidade" />
                    </SelectTrigger>
                    <SelectContent>
                      {DATE_MODES.map(m => (
                        <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {(formData.date_mode || 'fixed') === 'fixed' && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div>
                    <Label htmlFor="due_day">Vencimento (dia)</Label>
                    <Select value={formData.due_day} onValueChange={(value) => setFormData(prev => ({ ...prev, due_day: value }))}>
                      <SelectTrigger><SelectValue placeholder="Dia venc." /></SelectTrigger>
                      <SelectContent>
                        {DAYS.map(day => <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label htmlFor="billing_month">Mês Cobrança</Label>
                    <Select value={formData.billing_month} onValueChange={(value: BillingMonth) => setFormData(prev => ({ ...prev, billing_month: value }))}>
                      <SelectTrigger><SelectValue placeholder="Mês" /></SelectTrigger>
                      <SelectContent>
                        {getMonthOptions().map(month => <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              )}

              {(formData.date_mode || 'fixed') === 'period_due' && (
                <div className="space-y-4 p-4 border rounded-lg bg-gray-50/50">
                  <div>
                    <Label>Forma do período</Label>
                    <Select value={formData.period_due_mode || 'range'} onValueChange={(value: 'range' | 'days_back') => setFormData(prev => ({ ...prev, period_due_mode: value }))}>
                      <SelectTrigger><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {PERIOD_DUE_MODES.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </div>
                  {formData.period_due_mode === 'days_back' ? (
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <Label>Dias para trás</Label>
                        <Input type="number" min="1" value={formData.dias_para_tras ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, dias_para_tras: e.target.value }))} placeholder="Ex: 5" />
                      </div>
                      <div className="flex items-end pb-2">
                        <div className="flex items-center gap-2">
                          <Switch checked={formData.incluir_dia_atual ?? false} onCheckedChange={(c) => setFormData(prev => ({ ...prev, incluir_dia_atual: c }))} />
                          <Label>Incluir dia atual</Label>
                        </div>
                      </div>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-4">
                      <div>
                        <Label>Vencimento inicial (dia)</Label>
                        <Select value={formData.due_day_inicio ?? '1'} onValueChange={(v) => setFormData(prev => ({ ...prev, due_day_inicio: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DAYS.map(day => <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Vencimento final (dia)</Label>
                        <Select value={formData.due_day_fim ?? '31'} onValueChange={(v) => setFormData(prev => ({ ...prev, due_day_fim: v }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {DAYS.map(day => <SelectItem key={day.value} value={day.value}>{day.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label>Mês Cobrança</Label>
                        <Select value={formData.billing_month} onValueChange={(value: BillingMonth) => setFormData(prev => ({ ...prev, billing_month: value }))}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {getMonthOptions().map(month => <SelectItem key={month.value} value={month.value}>{month.label}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {(formData.date_mode || 'fixed') === 'period_overdue' && (
                <div className="grid grid-cols-2 gap-4 p-4 border rounded-lg bg-gray-50/50">
                  <div>
                    <Label>Cobrar a partir de (dias vencidos)</Label>
                    <Input type="number" min="0" value={formData.dias_vencidos_inicio ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, dias_vencidos_inicio: e.target.value }))} placeholder="Ex: 6" />
                  </div>
                  <div>
                    <Label>Até (dias para trás)</Label>
                    <Input type="number" min="0" value={formData.dias_vencidos_fim ?? ''} onChange={(e) => setFormData(prev => ({ ...prev, dias_vencidos_fim: e.target.value }))} placeholder="Ex: 60" />
                  </div>
                </div>
              )}

              <div className="mt-6 flex justify-between items-center p-4 bg-gray-50 rounded-lg">
                <div className="text-sm text-gray-700">
                  <span className="font-medium">Preview:</span> 
                  <Badge variant="outline" className="ml-2 font-mono">
                    {(formData.execution_mode || 'fixed_day') === 'frequency'
                      ? `Frequência: a cada ${formData.frequencia_intervalo || '?'} dia(s) a partir do dia ${formData.frequencia_dia_inicio || '1'}`
                      : (formData.date_mode || 'fixed') === 'period_overdue'
                        ? `DIA ${formData.day} • Vencidos ${formData.dias_vencidos_inicio || '?'}-${formData.dias_vencidos_fim || '?'} dias`
                        : (formData.date_mode || 'fixed') === 'period_due'
                          ? formData.period_due_mode === 'days_back'
                            ? `DIA ${formData.day} • Período ${formData.dias_para_tras || '?'} dias atrás`
                            : `DIA ${formData.day} • Venc. ${formData.due_day_inicio || '?'}-${formData.due_day_fim || '?'} (${formData.billing_month})`
                          : `DIA ${formData.day}, ${formData.event}, VENC ${formData.due_day}, MÊS ${formData.billing_month?.toUpperCase()}`}
                  </Badge>
                </div>
              </div>
            </div>
          )}

          <div className="mt-6 flex justify-end gap-2">
            {editingRuleId != null && (
              <Button
                onClick={handleCancelEdit}
                disabled={saving}
                variant="outline"
              >
                Cancelar
              </Button>
            )}
            <Button
              onClick={handleAddRule}
              disabled={saving}
              className="bg-green-600 hover:bg-green-700"
            >
              {saving ? (
                <>
                  <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                  Salvando...
                </>
              ) : editingRuleId != null ? (
                <>
                  <Edit className="w-4 h-4 mr-2" />
                  Salvar Alterações
                </>
              ) : (
                <>
                  <Plus className="w-4 h-4 mr-2" />
                  {activeCategory === 'LEMBRETE' ? 'Adicionar Lembrete' : 'Adicionar Regra'}
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card></div>
      )}

      {/* Lista de regras existentes - não mostrar para Aniversário, Pagamento ou Boas-Vindas */}
      {activeCategory !== 'ANIVERSÁRIO' && activeCategory !== 'PAGAMENTO' && activeCategory !== 'BOAS-VINDAS' && (
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {activeCategory === 'LEMBRETE' ? 'Lembretes Configurados' : 'Regras de Cobrança Configuradas'}
            <Badge variant="secondary" className="ml-2">
              {filteredBillingRules.length} {filteredBillingRules.length === 1 ? 'regra' : 'regras'}
            </Badge>
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto mb-2 text-gray-400" />
              <p>Carregando regras...</p>
            </div>
          ) : filteredBillingRules.length === 0 ? (
            <div className="text-center text-gray-500 py-8">
              <FileText className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>{activeCategory === 'LEMBRETE' ? 'Nenhum lembrete configurado' : 'Nenhuma regra de cobrança configurada'}</p>
              <p className="text-xs">Adicione sua primeira regra acima</p>
            </div>
          ) : (
            <div className="space-y-3">
              {filteredBillingRules.map((rule) => (
                <div 
                  key={rule.id} 
                  className={`flex items-center justify-between p-4 border-2 rounded-lg hover:border-gray-200 transition-colors ${
                    activeCategory === 'LEMBRETE' && !rule.is_active 
                      ? 'border-gray-200 bg-gray-50 opacity-60' 
                      : 'border-gray-100'
                  }`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-full ${activeCategory === 'LEMBRETE' ? 'bg-purple-100 text-purple-600' : getEventColor(rule.event)} flex items-center justify-center`}>
                      {activeCategory === 'LEMBRETE' ? <Bell className="h-5 w-5" /> : getEventIcon(rule.event)}
                    </div>
                    
                    <div>
                      <div className="flex items-center gap-2 flex-wrap">
                        {activeCategory === 'LEMBRETE' ? (
                          <>
                            <Badge variant="outline" className="bg-purple-100 text-purple-800 border-purple-300">
                              LEMBRETE
                            </Badge>
                            <Badge className="font-mono bg-blue-100 text-blue-800 border-blue-300">
                              {rule.days_before_due} {rule.days_before_due === 1 ? 'dia' : 'dias'} antes do vencimento
                            </Badge>
                            <Badge variant={rule.is_active ? 'default' : 'secondary'}>
                              {rule.is_active ? 'Ativo' : 'Inativo'}
                            </Badge>
                          </>
                        ) : (
                          <>
                            {(rule.execution_mode || 'fixed_day') === 'frequency' ? (
                              <Badge variant="secondary" className="font-mono">
                                Freq. {rule.frequencia_intervalo ?? '?'} em {rule.frequencia_intervalo ?? '?'} dias
                              </Badge>
                            ) : (
                              <Badge className="font-mono">DIA {rule.day}</Badge>
                            )}
                            <Badge variant="outline" className={getEventLightColor(rule.event)}>
                              {rule.event}
                            </Badge>
                            {(rule.date_mode || 'fixed') === 'period_overdue' ? (
                              <Badge variant="secondary">Vencidos {rule.dias_vencidos_inicio ?? '?'}-{rule.dias_vencidos_fim ?? '?'} dias</Badge>
                            ) : (rule.date_mode || 'fixed') === 'period_due' ? (
                              rule.period_due_mode === 'days_back' ? (
                                <Badge variant="secondary">Período {rule.dias_para_tras ?? '?'} dias atrás</Badge>
                              ) : (
                                <Badge variant="secondary">Período venc. {rule.due_day_inicio ?? '?'}-{rule.due_day_fim ?? '?'}</Badge>
                              )
                            ) : (rule.execution_mode || 'fixed_day') === 'frequency' ? (
                              <Badge variant="secondary">A cada {rule.frequencia_intervalo ?? '?'} dia(s) a partir do {rule.frequencia_dia_inicio ?? '1'}</Badge>
                            ) : (
                              <>
                                <Badge variant="secondary">VENC: {rule.due_day}</Badge>
                                <Badge variant={rule.billing_month === 'atual' ? 'default' : 'destructive'}>
                                  {getBillingMonthLabel(rule.billing_month)}
                                </Badge>
                              </>
                            )}
                          </>
                        )}
                      </div>
                      <p className="text-sm text-gray-600 mt-1">
                        {activeCategory === 'LEMBRETE' 
                          ? 'Envio de lembrete de vencimento'
                          : getEventDescription(rule.event)
                        }
                        {activeCategory === 'LEMBRETE' && (
                          <span className="ml-2 text-blue-600">
                            • Enviado {rule.days_before_due} {rule.days_before_due === 1 ? 'dia' : 'dias'} antes do vencimento
                          </span>
                        )}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-3">
                    {activeCategory === 'LEMBRETE' && (
                      <div className="flex items-center gap-2">
                        <Label htmlFor={`toggle-${rule.id}`} className="text-sm cursor-pointer">
                          {rule.is_active ? 'Ativo' : 'Inativo'}
                        </Label>
                        <Switch
                          id={`toggle-${rule.id}`}
                          checked={rule.is_active}
                          onCheckedChange={() => handleToggleReminderActive(rule.id, rule.is_active)}
                          disabled={saving}
                        />
                      </div>
                    )}
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => activeCategory === 'LEMBRETE'
                        ? startEditReminder(rule as unknown as ReminderRule)
                        : startEditBilling(rule)
                      }
                      disabled={saving}
                      title="Editar"
                    >
                      <Edit className="h-4 w-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="destructive"
                      onClick={() => handleDeleteRule(rule.id)}
                      disabled={saving}
                      className="hover:bg-red-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
      )}
      </>
      )}

      {/* Modais */}
      {client && (
        <>
          <BlockDayModal
            open={blockDayModalOpen}
            onClose={() => setBlockDayModalOpen(false)}
            onConfirm={handleBlockDay}
            clientId={client.id}
            year={calendarHook.currentDate.getFullYear()}
            month={calendarHook.currentDate.getMonth() + 1}
          />

          <RescheduleEventModal
            open={rescheduleModalOpen}
            onClose={() => {
              setRescheduleModalOpen(false);
              setSelectedEvent(null);
            }}
            onConfirm={handleConfirmReschedule}
            event={selectedEvent}
            clientId={client.id}
            year={calendarHook.currentDate.getFullYear()}
            month={calendarHook.currentDate.getMonth() + 1}
            blockedDays={calendarHook.calendarData?.blockedDays || []}
          />
        </>
      )}
    </TabsContent>
  );
};

export default BillingTab; 